import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './AccountSettingsPage.css';

const AUTH_BASE = process.env.REACT_APP_AUTH_URL || 'http://localhost:5000/auth';
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface BillingDetails {
  plan_interval?: string;
  period_end?: number;
  start_date?: number;
  payment_method?: { brand: string; last4: string };
}

interface Invoice {
  id: string;
  date: number;
  amount: number;
  currency: string;
  status: string;
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
}

const AccountSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [showDeletePassword, setShowDeletePassword] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Cancel subscription state
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState('');
  const [cancelError, setCancelError] = useState('');

  // Subscription state
  const [searchParams] = useSearchParams();
  const [subscriptionSuccess, setSubscriptionSuccess] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [billingDetails, setBillingDetails] = useState<BillingDetails | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  const isSubscribed = user?.subscription_status === 'active' || user?.subscription_status === 'trialing' || user?.subscription_status === 'past_due';

  const isCanceled = user?.subscription_status === 'canceled';

  // Accordion state
  const [expandedSection, setExpandedSection] = useState<string>('billing');

  const toggleSection = (section: string) => {
    if (expandedSection === 'account' && section !== 'account') {
      setShowDeleteConfirm(false);
      setDeletePassword('');
      setDeleteError('');
      setCancelError('');
    }
    setExpandedSection(prev => prev === section ? '' : section);
  };

  useEffect(() => {
    if (searchParams.get('subscription') === 'success') {
      setSubscriptionSuccess('Subscription activated successfully! Welcome aboard.');
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isSubscribed) return;

    axios.get(`${API_BASE}/subscription/details`)
      .then(res => setBillingDetails(res.data))
      .catch(() => {});

    axios.get(`${API_BASE}/subscription/invoices`)
      .then(res => setInvoices(res.data.invoices || []))
      .catch(() => {});
  }, [isSubscribed]);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.post(`${AUTH_BASE}/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPasswordSuccess('Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPasswordError(err.response?.data?.error || 'Failed to change password.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelError('');
    setCancelLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/subscription/cancel`);
      const periodEnd = res.data.period_end
        ? new Date(res.data.period_end * 1000).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric'
          })
        : formatDate(user?.subscription_period_end);
      setCancelSuccess(`Your subscription has been canceled. You can continue using CompanySieve until ${periodEnd}.`);
    } catch (err: any) {
      setCancelError(err.response?.data?.error || 'Failed to cancel subscription.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');
    setDeleteLoading(true);
    try {
      await axios.delete(`${AUTH_BASE}/account`, {
        data: { password: deletePassword },
      });
      await logout();
      navigate('/');
    } catch (err: any) {
      setDeleteError(err.response?.data?.error || 'Failed to delete account.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/subscription/portal`);
      window.location.href = response.data.url;
    } catch (err: any) {
      console.error('Failed to open billing portal');
      setPortalLoading(false);
    }
  };

  const formatDate = (iso: string | null | undefined) => {
    if (!iso) return 'N/A';
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const formatTimestamp = (ts: number | null | undefined) => {
    if (!ts) return 'N/A';
    return new Date(ts * 1000).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  return (
    <div className="settings-page">
      <h1>Account Settings</h1>

      {/* Subscription & Billing */}
      <div className="settings-accordion">
        <button
          className={`accordion-header${expandedSection === 'billing' ? ' expanded' : ''}`}
          onClick={() => toggleSection('billing')}
        >
          <span>Subscription &amp; Billing</span>
          <span className={`accordion-chevron${expandedSection === 'billing' ? ' expanded' : ''}`} />
        </button>
        {expandedSection === 'billing' && (
          <div className="accordion-body">
            {subscriptionSuccess && <div className="settings-success">{subscriptionSuccess}</div>}

            <div className="subscription-info">
              <div className="info-row">
                <span className="info-label">Status</span>
                <span className={`info-value status-${user?.subscription_status || 'none'}`}>
                  {(user?.subscription_status || 'none').replace('_', ' ').toUpperCase()}
                </span>
              </div>
              {user?.plan_interval && (
                <div className="info-row">
                  <span className="info-label">Plan</span>
                  <span className="info-value">
                    {user.plan_interval === 'year' ? 'Annual ($2,500/yr)' : 'Monthly ($250/mo)'}
                  </span>
                </div>
              )}
              {billingDetails?.payment_method && (
                <div className="info-row">
                  <span className="info-label">Payment method</span>
                  <span className="info-value">
                    {billingDetails.payment_method.brand} ending in {billingDetails.payment_method.last4}
                  </span>
                </div>
              )}
              {billingDetails?.start_date && (
                <div className="info-row">
                  <span className="info-label">Member since</span>
                  <span className="info-value">{formatTimestamp(billingDetails.start_date)}</span>
                </div>
              )}
              {user?.subscription_period_end && (
                <div className="info-row">
                  <span className="info-label">Next billing date</span>
                  <span className="info-value">{formatDate(user.subscription_period_end)}</span>
                </div>
              )}
            </div>

            {isSubscribed ? (
              <button
                className="settings-button primary"
                onClick={handleManageSubscription}
                disabled={portalLoading}
              >
                {portalLoading ? 'Opening...' : 'Manage Subscription'}
              </button>
            ) : (
              <p style={{ fontSize: '14px', color: '#718096', marginTop: '12px' }}>
                No active subscription. Visit the home page to subscribe.
              </p>
            )}

            {isSubscribed && (
              <>
                <hr className="billing-divider" />
                <h3 className="accordion-subheading">Invoice History</h3>
                {invoices.length === 0 ? (
                  <p style={{ fontSize: '14px', color: '#718096' }}>No invoices yet.</p>
                ) : (
                  <table className="invoice-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Receipt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map(inv => (
                        <tr key={inv.id}>
                          <td>{formatTimestamp(inv.date)}</td>
                          <td>{formatCurrency(inv.amount, inv.currency)}</td>
                          <td>
                            <span className={`invoice-status invoice-status-${inv.status}`}>
                              {inv.status}
                            </span>
                          </td>
                          <td>
                            {inv.hosted_invoice_url ? (
                              <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                                View
                              </a>
                            ) : (
                              '\u2014'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Security */}
      <div className="settings-accordion">
        <button
          className={`accordion-header${expandedSection === 'security' ? ' expanded' : ''}`}
          onClick={() => toggleSection('security')}
        >
          <span>Password Reset</span>
          <span className={`accordion-chevron${expandedSection === 'security' ? ' expanded' : ''}`} />
        </button>
        {expandedSection === 'security' && (
          <div className="accordion-body">
            {passwordSuccess && <div className="settings-success">{passwordSuccess}</div>}
            {passwordError && <div className="settings-error">{passwordError}</div>}

            <form onSubmit={handleChangePassword}>
              <div className="form-group">
                <label htmlFor="current-password">Current Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    id="current-password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="new-password">New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    id="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    placeholder="At least 6 characters"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirm-password">Confirm New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirm-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="settings-button primary"
                disabled={passwordLoading}
              >
                {passwordLoading ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Account */}
      <div className="settings-accordion">
        <button
          className={`accordion-header${expandedSection === 'account' ? ' expanded' : ''}`}
          onClick={() => toggleSection('account')}
        >
          <span>Cancel Account</span>
          <span className={`accordion-chevron${expandedSection === 'account' ? ' expanded' : ''}`} />
        </button>
        {expandedSection === 'account' && (
          <div className="accordion-body">
            {/* Section A: Cancel Subscription */}
            {isSubscribed && !cancelSuccess && (
              <div className="cancel-subscription-section">
                <div className="remaining-time">
                  Your subscription is paid through <strong>{formatDate(user?.subscription_period_end)}</strong>.
                </div>
                {cancelError && <div className="settings-error">{cancelError}</div>}
                <button
                  className="settings-button primary"
                  onClick={handleCancelSubscription}
                  disabled={cancelLoading}
                >
                  {cancelLoading ? 'Canceling...' : 'Cancel Subscription'}
                </button>
                <p className="cancel-explanation">
                  After canceling, you can continue using CompanySieve through the end of your billing period.
                  Your account and screening history will not be deleted. You can resubscribe at any time to
                  regain full access.
                </p>
              </div>
            )}

            {cancelSuccess && (
              <div className="cancel-subscription-section">
                <div className="settings-success">{cancelSuccess}</div>
              </div>
            )}

            {isCanceled && user?.subscription_period_end && !cancelSuccess && (
              <div className="cancel-subscription-section">
                <div className="remaining-time">
                  Your subscription is canceled. Access continues until <strong>{formatDate(user.subscription_period_end)}</strong>.
                </div>
              </div>
            )}

            {(isSubscribed || isCanceled || cancelSuccess) && <hr className="billing-divider" />}

            {/* Section B: Delete Account */}
            <p className="danger-warning">
              This action is permanent and cannot be undone. All your screening history will be deleted.
              Please download any reports you need before proceeding.
            </p>

            {isSubscribed && (
              <p className="danger-warning" style={{ fontWeight: 600 }}>
                You have an active subscription paid through {formatDate(user?.subscription_period_end)}.
                Deleting your account will cancel it immediately � no refund for remaining time.
              </p>
            )}

            {deleteError && <div className="settings-error">{deleteError}</div>}

            {!showDeleteConfirm ? (
              <button
                className="danger-button"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete My Account
              </button>
            ) : (
              <div className="delete-confirm">
                <p>Enter your password to confirm deletion:</p>
                <div className="form-group">
                  <div className="password-input-wrapper">
                    <input
                      type={showDeletePassword ? 'text' : 'password'}
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      placeholder="Your password"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowDeletePassword(!showDeletePassword)}
                    >
                      {showDeletePassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>
                <div className="delete-confirm-actions">
                  <button
                    className="cancel-button"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeletePassword('');
                      setDeleteError('');
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="danger-button"
                    onClick={handleDeleteAccount}
                    disabled={deleteLoading || !deletePassword}
                  >
                    {deleteLoading ? 'Deleting...' : 'Confirm Delete'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountSettingsPage;